import React, { useState, useRef, useEffect } from 'react';
import { Home, Settings, User, MessageCircle, Plus, PanelLeftClose, PanelLeft, LogOut, X, BarChart3, TrendingUp, LineChart } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

import { useAuth } from '../../../contexts/AuthContext';
import { useGlobalSession } from '../../../contexts/GlobalSessionContext';
import { useLayoutOptional } from '../../../contexts/LayoutContext';

import { ChatOptionsMenu } from './ChatOptionsMenu';
import { DataLibrary } from './DataLibrary';
import { SettingsModal } from '../../SettingsModal';
import styles from './SideNavigationMenu.module.css';
import { XquantLogo } from '../../../assets/XquantLogo';
import logger from '../../../logging/logger';

interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
}

interface SideNavigationMenuProps {
  items?: NavigationItem[];
  isOpen?: boolean;
  isCollapsed?: boolean;
  lockCollapsed?: boolean;
  isMobileOpen?: boolean;
  onToggle?: (isOpen: boolean) => void;
  onCollapse?: (isCollapsed: boolean) => void;
  onMobileClose?: () => void;
}

const SideNavigationMenu: React.FC<SideNavigationMenuProps> = ({
  items,
  isOpen: controlledIsOpen,
  isCollapsed: controlledIsCollapsed,
  lockCollapsed = false,
  isMobileOpen = false,
  onCollapse,
  onMobileClose
}) => {
  const [internalIsOpen] = useState(true);
  const [internalIsCollapsed, setInternalIsCollapsed] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsInitialTab, setSettingsInitialTab] = useState<'general' | 'profile' | 'notifications' | 'privacy'>('general');
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 2;

  const navItemsRef = useRef<HTMLDivElement>(null);
  const { isAuthenticated, user, logout } = useAuth();
  const {
    sessions: userSessions,
    isLoading,
    isLoadingMore,
    hasMoreSessions,
    loadMoreSessions,
    archiveSession,
    renameSession,
    requestDelete
  } = useGlobalSession();
  
  const navigate = useNavigate();
  const location = useLocation();
  const layoutCtx = useLayoutOptional();

  /**
   * Activate a layout tab by name. If the layout context is available
   * and a tab with the given name exists, dispatch SWITCH_TAB.
   */
  const activateTabByName = (tabName: string) => {
    if (!layoutCtx) return;
    const tab = layoutCtx.state.tabs.find((t) => t.name === tabName);
    if (tab) {
      layoutCtx.dispatch({ type: 'SWITCH_TAB', payload: { tabId: tab.id } });
    }
  };

  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const isCollapsed = controlledIsCollapsed !== undefined ? controlledIsCollapsed : internalIsCollapsed;

  const handleUserMenuToggle = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleOpenSettings = () => {
    setShowUserMenu(false);
    setSettingsInitialTab('general');
    setShowSettings(true);
  };

  const handleOpenUserAnalytics = () => {
    setShowUserMenu(false);
    setSettingsInitialTab('profile');
    setShowSettings(true);
  };

  const handleCollapse = () => {
    const newState = !isCollapsed;
    if (onCollapse) {
      onCollapse(newState);
    } else {
      setInternalIsCollapsed(newState);
    }
  };

  const handleNewChat = async () => {
    if (!isAuthenticated || !user) return;
    // Close mobile sidebar when navigating
    if (isMobileOpen && onMobileClose) {
      onMobileClose();
    }
    navigate('/chat');
  };

  const handleChatClick = (sessionId: string) => {
    if (navItemsRef.current) {
      const scrollPosition = navItemsRef.current.scrollTop;
      sessionStorage.setItem('sidebarScrollPosition', scrollPosition.toString());
    }
    // Close mobile sidebar when navigating
    if (isMobileOpen && onMobileClose) {
      onMobileClose();
    }
    navigate(`/${sessionId}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/home');
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const getUserInitial = () => {
    if (user?.name) {
      return user.name.charAt(0).toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return 'U';
  };

  // Reset image error when user changes
  useEffect(() => {
    if (user?.picture) {
      logger.info('🔄 User picture URL detected:', {
        url: user.picture,
        userSub: user.sub,
        userName: user.name,
        userEmail: user.email
      });
      
      fetch(user.picture, { method: 'HEAD', mode: 'no-cors' })
        .then(() => {
          logger.info('✅ Image URL is reachable via fetch');
        })
        .catch((error) => {
          logger.error('❌ Image URL fetch failed:', error);
        });
      
      setImageError(false);
      setImageLoading(true);
      setRetryCount(0);
    } else {
      logger.info('❌ No user picture URL available:', {
        hasUser: !!user,
        userSub: user?.sub,
        userName: user?.name,
        userEmail: user?.email
      });
    }
  }, [user]);

  const handleImageError = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const img = event.currentTarget;
    logger.error('🖼️ Avatar image failed to load (attempt ' + (retryCount + 1) + '):', {
      src: img.src,
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight,
      complete: img.complete,
      error: event.type,
      userPicture: user?.picture,
      retryCount: retryCount
    });
    
    if (retryCount < maxRetries && user?.picture) {
      logger.info('🔄 Retrying image load with cache-busting...');
      setRetryCount(prev => prev + 1);
      const cacheBustedUrl = user.picture + (user.picture.includes('?') ? '&' : '?') + '_t=' + Date.now();
      img.src = cacheBustedUrl;
      return;
    }
    
    setImageError(true);
    setImageLoading(false);
  };

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const img = event.currentTarget;
    logger.info('✅ Avatar image loaded successfully:', {
      src: img.src,
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight,
      complete: img.complete,
      userPicture: user?.picture
    });
    setImageError(false);
    setImageLoading(false);
  };

  const shouldShowImage = user?.picture && !imageError;

  // Close user menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showUserMenu && !(event.target as Element).closest('.userProfileContainer')) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  const isTradingRoute = location.pathname === '/trading' || location.pathname === '/dashboard';
  const isMarketMonitorRoute = location.pathname === '/market-monitor';
  const isStrategySystematicsRoute = location.pathname === '/strategy-systematics';
  const isJobsRoute = location.pathname === '/jobs';
  const useChatNavShortcut = isTradingRoute || isMarketMonitorRoute || isStrategySystematicsRoute || isJobsRoute;

  const defaultItems: NavigationItem[] = [
    useChatNavShortcut
      ? {
          id: 'chat',
          label: 'Chat',
          icon: <MessageCircle className="h-5 w-5" />,
          onClick: () => {
            if (isMobileOpen && onMobileClose) onMobileClose();
            navigate('/chat');
          }
        }
      : {
          id: 'new-chat',
          label: 'New Chat',
          icon: <Plus className="h-5 w-5" />,
          onClick: handleNewChat
        },
    {
      id: 'home',
      label: 'Home',
      icon: <Home className="h-5 w-5" />,
      onClick: () => {
        if (isMobileOpen && onMobileClose) onMobileClose();
        navigate('/home');
      }
    },
    ...(!isTradingRoute ? [{
      id: 'trading-monitor',
      label: 'Trading Dashboard',
      icon: <TrendingUp className="h-5 w-5" />,
      onClick: () => {
        if (isMobileOpen && onMobileClose) onMobileClose();
        activateTabByName('Paper Trade');
        navigate('/trading');
      }
    }] : []),
    ...(!isMarketMonitorRoute ? [{
      id: 'market-monitor',
      label: 'Market Monitor',
      icon: <LineChart className="h-5 w-5" />,
      onClick: () => {
        if (isMobileOpen && onMobileClose) onMobileClose();
        navigate('/market-monitor');
      }
    }] : []),
    ...(!isStrategySystematicsRoute ? [{
      id: 'strategy-systematics',
      label: 'Strategy Systematics',
      icon: <BarChart3 className="h-5 w-5" />,
      onClick: () => {
        if (isMobileOpen && onMobileClose) onMobileClose();
        navigate('/strategy-systematics');
      }
    }] : []),
  ];

  const navigationItems = items || defaultItems;

  // Restore scroll position after navigation
  useEffect(() => {
    const navItemsContainer = navItemsRef.current;
    if (!navItemsContainer) return;

    const savedScrollPosition = sessionStorage.getItem('sidebarScrollPosition');
    if (savedScrollPosition) {
      setTimeout(() => {
        navItemsContainer.scrollTop = parseInt(savedScrollPosition, 10);
        sessionStorage.removeItem('sidebarScrollPosition');
      }, 100);
    }
  }, [location.pathname, userSessions.length]);

  // Infinite scroll: detect when user scrolls near bottom of sessions list
  useEffect(() => {
    const navItemsContainer = navItemsRef.current;
    if (!navItemsContainer) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = navItemsContainer;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      
      if (distanceFromBottom < 100 && hasMoreSessions && !isLoadingMore) {
        loadMoreSessions();
      }
    };

    navItemsContainer.addEventListener('scroll', handleScroll);
    return () => navItemsContainer.removeEventListener('scroll', handleScroll);
  }, [hasMoreSessions, isLoadingMore, loadMoreSessions]);

  if (!isOpen) return null;

  return (
    <>
      {/* Mobile overlay */}
      <div 
        className={`${styles.mobileOverlay} ${isMobileOpen ? styles.visible : ''}`}
        onClick={onMobileClose}
      />
      <nav className={`${styles.sideNav} ${isCollapsed && !isMobileOpen ? styles.collapsed : ''} ${isMobileOpen ? styles.mobileOpen : ''}`}>
      {/* Header with logo and collapse/close button */}
      <div className={styles.header}>
        <div className={styles.logoContainer}>
          {(!isCollapsed || isMobileOpen) && (
            <button
              type="button"
              className={styles.logoWrapper}
              onClick={() => {
                if (isMobileOpen && onMobileClose) onMobileClose();
                navigate('/home');
              }}
              aria-label="Go to homepage"
            >
              <XquantLogo size={28} />
              <span className={styles.logoText}>xquant</span>
            </button>
          )}
        </div>
        {/* Mobile close button */}
        {isMobileOpen ? (
          <button
            className={styles.closeButton}
            onClick={onMobileClose}
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        ) : lockCollapsed ? (
          <button
            type="button"
            className={styles.lockedLogo}
            onClick={() => navigate('/home')}
            aria-label="Go to homepage"
          >
            <XquantLogo size={24} />
          </button>
        ) : (
          <button
            className={styles.collapseButton}
            onClick={handleCollapse}
            onMouseEnter={() => isCollapsed && setHoveredItem('collapse')}
            onMouseLeave={() => setHoveredItem(null)}
            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <>
                {hoveredItem === 'collapse' ? (
                  <PanelLeft className="h-5 w-5" />
                ) : (
                  <XquantLogo size={24} />
                )}
                {hoveredItem === 'collapse' && (
                  <div className={styles.tooltip}>Expand sidebar</div>
                )}
              </>
            ) : (
              <PanelLeftClose className="h-5 w-5" />
            )}
          </button>
        )}
      </div>

      {/* Navigation Items */}
      <div className={styles.navItems} ref={navItemsRef}>
        {navigationItems.map((item) => (
          <button
            key={item.id}
            className={styles.navItem}
            onClick={item.onClick}
            onMouseEnter={() => isCollapsed && !isMobileOpen && setHoveredItem(item.id)}
            onMouseLeave={() => setHoveredItem(null)}
          >
            {item.icon}
            {(!isCollapsed || isMobileOpen) && <span>{item.label}</span>}
            {isCollapsed && !isMobileOpen && hoveredItem === item.id && (
              <div className={styles.navItemTooltip}>{item.label}</div>
            )}
          </button>
        ))}

        {/* User Sessions Section */}
        {isAuthenticated && (!isCollapsed || isMobileOpen) && userSessions.length > 0 && (
          <>
            <div className={styles.sectionDivider}>
              <span>Recent Chats</span>
            </div>
            {isLoading ? (
              <div className={styles.navItem}>
                <MessageCircle className="h-5 w-5" />
                <span>Loading...</span>
              </div>
            ) : (
              <>
                {userSessions.map((session) => {
                  const isActive = location.pathname === `/${session.id}`;
                  return (
                    <div
                      key={session.id}
                      className={`${styles.chatItemContainer} ${isActive ? styles.active : ''}`}
                    >
                      <button
                        className={styles.navItem}
                        onClick={() => handleChatClick(session.id)}
                        title={session.title}
                      >
                        <span className={styles.chatTitle}>{session.title}</span>
                      </button>
                      <ChatOptionsMenu
                        sessionId={session.id}
                        currentTitle={session.title}
                        onRename={renameSession}
                        onArchive={archiveSession}
                        onRequestDelete={requestDelete}
                        activeMenuId={activeMenuId}
                        setActiveMenuId={setActiveMenuId}
                      />
                    </div>
                  );
                })}
                {isLoadingMore && (
                  <div className={styles.navItem}>
                    <MessageCircle className="h-5 w-5" />
                    <span>Loading more...</span>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* User Profile Section at Bottom */}
      <div className={styles.userSection}>
        {isAuthenticated ? (
          <div className={`${styles.userProfileContainer} userProfileContainer`}>
            <button
              className={styles.userProfile}
              onClick={handleUserMenuToggle}
            >
              <div className={styles.userButton}>
                <div className={styles.userAvatar}>
                  {shouldShowImage ? (
                    <img 
                      src={user.picture} 
                      alt="User" 
                      className={`${styles.avatarImage} ${imageLoading ? styles.loading : ''}`}
                      onError={handleImageError}
                      onLoad={handleImageLoad}
                      crossOrigin="anonymous"
                      referrerPolicy="no-referrer"
                    />
                  ) : null}
                  {(!shouldShowImage || imageLoading) && (
                    <span className={styles.avatarInitial}>{getUserInitial()}</span>
                  )}
                </div>
                {(!isCollapsed || isMobileOpen) && (
                  <div className={styles.userInfo}>
                    <span className={styles.userName}>{user?.name || user?.email}</span>
                  </div>
                )}
              </div>

              {showUserMenu && (
                <div className={styles.userDropdown}>
                  <div className={styles.userInfoSection}>
                    <div className={styles.userAvatar}>
                      {shouldShowImage ? (
                        <img 
                          src={user.picture} 
                          alt="User" 
                          className={`${styles.avatarImage} ${imageLoading ? styles.loading : ''}`}
                          onError={handleImageError}
                          onLoad={handleImageLoad}
                          crossOrigin="anonymous"
                          referrerPolicy="no-referrer"
                        />
                      ) : null}
                      {(!shouldShowImage || imageLoading) && (
                        <span className={styles.avatarInitial}>{getUserInitial()}</span>
                      )}
                    </div>
                    <div className={styles.userDetails}>
                      <div className={styles.userFullName}>{user?.name || 'User'}</div>
                      <div className={styles.userEmail}>{user?.email}</div>
                    </div>
                  </div>
                  <div className={styles.dropdownDivider}></div>
                  <button className={styles.dropdownItem} onClick={handleOpenUserAnalytics}>
                    <BarChart3 className="h-4 w-4" />
                    <span>Tier and Usage Analytics</span>
                  </button>
                  <button className={styles.dropdownItem} onClick={handleOpenSettings}>
                    <Settings className="h-4 w-4" />
                    <span>Settings</span>
                  </button>
                  <DataLibrary onClose={() => setShowUserMenu(false)} />
                  <div className={styles.dropdownDivider}></div>
                  <button className={styles.dropdownItem} onClick={handleLogout}>
                    <LogOut className="h-4 w-4" />
                    <span>Logout</span>
                  </button>
                </div>
              )}
            </button>
          </div>
        ) : (
          <button
            className={styles.loginButton}
            onClick={handleLogin}
            title={isCollapsed ? "Login" : undefined}
          >
            <User className="h-5 w-5" />
            {!isCollapsed && <span>Login</span>}
          </button>
        )}
      </div>

      <SettingsModal 
        isOpen={showSettings} 
        onClose={() => setShowSettings(false)} 
        initialSection={settingsInitialTab}
      />
    </nav>
    </>
  );
};

export { SideNavigationMenu };
